// @ts-strict-ignore
import {reporter} from 'common/report/error-reporting';
import {getProjectId, getWorkspaceId} from 'common/report/util';
import {objToQueryString} from 'common/util';
import {apiClient} from 'common/util/api-client';
import {ApiResourceClient} from 'common/util/model-resource/api-resource';

import {appApiUrl} from '../utils';
import {purgeFetchCache} from '../utils/fetch-cache';

export type LookupTableDefinition = {
  createdAt: string;
  id: string;
  lastModifiedAt: string;
  name: string;
  token: string;
  hasMappedProperties: boolean;
  createdBy: {
    name: string;
    email: string;
    userId: number;
  };
  description: string;
  entityID: number;
  can_update_basic: boolean;
  can_view: boolean;
  allow_staff_override: boolean;
  is_superadmin: boolean;
  can_share: boolean;
  canDeleteLookupTable: boolean;
};

export function buildEndpoint({
  entity,
  searchParams,
}: {entity?: string; searchParams?: Record<string, string>} = {}): string {
  return (
    appApiUrl(`data-definitions/lookup-tables` + (entity ? `/${entity}` : ``), {
      projectId: getProjectId(),
      workspaceId: getWorkspaceId(),
    }) + (searchParams ? `?${objToQueryString(searchParams)}` : ``)
  );
}

export function getById(id: string) {
  return apiClient.get<LookupTableDefinition[]>(buildEndpoint(), {'data-group-id': id});
}

export async function updateLookupTableDefinition(
  dataGroupId: string,
  propsToUpdate: Partial<LookupTableDefinition>,
): Promise<void> {
  try {
    await apiClient.patch(buildEndpoint(), {...propsToUpdate, 'data-group-id': dataGroupId});
  } catch (error) {
    reporter.error(error, `updateLookupTableDefinition error`, {'data-group-id': dataGroupId, propsToUpdate});
    throw error;
  }
}

export async function deleteLookupTableDefinitions(dataGroupIds: string[]): Promise<void> {
  try {
    await apiClient.delete(buildEndpoint(), {body: JSON.stringify({'data-group-ids': dataGroupIds})});
  } catch (error) {
    reporter.error(error, `deleteLookupTableDefinition error`, {'data-group-ids': dataGroupIds});
    throw error;
  }
}

export function getLookupTableDownloadUrl(dataGroupId: string): Promise<string> {
  return apiClient.get(buildEndpoint({entity: `download-url`, searchParams: {'data-group-id': dataGroupId}}));
}

const fetchLookupTables = (indexType: `cache` | `network`) => {
  const apiClientGet = apiClient.hydratedGet(buildEndpoint());

  const transform = (lookupTables) => {
    if (lookupTables) {
      return lookupTables.reduce((acc, lookupTable) => {
        acc[lookupTable.id] = lookupTable;
        return acc;
      }, {});
    }
    return {};
  };

  if (indexType === `cache`) {
    return apiClientGet.cache.then((lookupTables: LookupTableDefinition[]) => transform(lookupTables));
  }

  return apiClientGet.network.then((lookupTables: LookupTableDefinition[]) => transform(lookupTables));
};

export const lookupTablesClient: ApiResourceClient<LookupTableDefinition> = {
  index() {
    return {
      cache: fetchLookupTables(`cache`),
      network: fetchLookupTables(`network`),
    };
  },

  get(dataGroupId: string) {
    return apiClient.hydratedGet(buildEndpoint(), undefined, {body: JSON.stringify({'data-group-id': dataGroupId})});
  },

  purgeCache() {
    purgeFetchCache({everything: true, path: buildEndpoint()});
  },
};
